﻿export type AssociationMeta = {
  shortName: string
  fullName: string
  establishedYear: number
  currentYear: number
  anniversaryLabel: string
  heroTitle: string
  heroSubtitle: string
  mission: string
  description: string
}

export type StatItem = {
  label: string
  value: string
  detail: string
}

export type ProjectStory = {
  slug: string
  name: string
  subtitle: string
  category: string
  startYear: number
  summary: string
  impact: string[]
  highlights: string[]
  subProjects?: Array<{
    name: string
    startYear?: number
    summary: string
  }>
  sections?: Array<{
    title: string
    body: string
  }>
  sourceNote?: string
  image?: string
  caption?: string
}

export type TimelineEvent = {
  year: string
  title: string
  description: string
  badge?: string
}

export type DepartmentInfo = {
  name: string
  focus: string
  description: string
}

export type AnnualMoment = {
  title: string
  context: string
  description: string
  tag: string
}

export type InternalEvent = {
  id: string
  title: string
  date: string
  intro: string
  highlights: string[]
  keyPeople: string[]
  closing: string
  coverImage?: string
}

export type GalleryStory = {
  title: string
  summary: string
  tags: string[]
  caption: string
  sourceNote?: string
}

export type FeatureBlock = {
  title: string
  summary: string
  bullets: string[]
}

export type TeachingMediaHit = {
  platform: string
  angle: string
  reach: string
  value: string
}

export type TeachingBaseProfile = {
  name: string
  location: string
  since: string
  theme: string
  summary: string
  metrics: string[]
  highlights: string[]
  background: string
  practice: string
  impact: string
  mediaSummary: string
  mediaHits: TeachingMediaHit[]
}

export const associationMeta: AssociationMeta = {
  shortName: '自动化青协',
  fullName: '杭州电子科技大学自动化学院（人工智能学院）青年志愿者协会',
  establishedYear: 2002,
  currentYear: 2025,
  anniversaryLabel: '第二十三载',
  heroTitle: '二十三载自青路，砥砺赓续志愿情',
  heroSubtitle: '63 名成员、6 个部门、5103 名注册志愿者共同参与，从社区、病房、慧灵基地到赛会与乡村课堂，把志愿服务写进校园与社会。',
  mission: '服务同学，奉献社会',
  description:
    '自动化青协成立于 2002 年，始终坚持“奉献、友爱、互助、进步”的准则，围绕社区服务、病房陪伴、慧灵助残、乡村支教、校园空间运营与大型赛会保障持续开展志愿行动，累计注册志愿者 5103 人、服务时长超过 80024.7 小时。'
}

export const homeStats: StatItem[] = [
  {
    label: '注册志愿者',
    value: '5103 人',
    detail: '长期参与自动化青协各类基地、赛会、校园与支教项目'
  },
  {
    label: '累计时长',
    value: '80024.7 小时',
    detail: '截至 2025 年形成的真实志愿服务总量，覆盖社区、病房、慧灵与赛会等场景'
  },
  {
    label: '2025 年行动',
    value: '197 场',
    detail: '全年有效活动覆盖环境卫生、扶残助残、便民生活、禁毒宣传等多个领域'
  },
  {
    label: '组织结构',
    value: '63 名成员',
    detail: '由两位指导老师带领，下设办公室、组织部、新媒体部、项目与基地拓展、志管中心六个部门'
  }
]

export const signatureProjects: ProjectStory[] = [
  {
    slug: 'lingdongyixing',
    name: '灵动翼行',
    subtitle: '十一载步履不停，我们携爱共进',
    category: '助残关怀',
    startYear: 2014,
    summary:
      '由杭州慧灵机构与自动化青协共同发起，面向心智残障人士和精神康复者，围绕陪伴、兴趣培养、教育支持与社会融合开展长期服务。',
    impact: ['2014-2025', '535 次服务', '2800+ 志愿者', '15525.8 小时', '5000+ 直接受益人次'],
    highlights: ['2021 年获精品立项', '2021 年获校优秀志愿基地', '5 个慧灵合作基地', '形成学校、基地、青年三方受益结构']
    ,
    sections: [
      {
        title: '项目概况',
        body:
          '“灵动翼行”于 2014 年启动，由杭州慧灵机构和自动化青协共同发起，主要面向杭州大龄心智残障人士和精神康复者群体。项目围绕日常关怀、文体活动、兴趣培养与教育支持等内容展开，初心是陪伴服务对象共同成长，并支持他们更好融入社会。'
      },
      {
        title: '长期服务方式',
        body:
          '十一年来，志愿者团队长期保持前往多个慧灵基地的稳定节奏。活动从生理健康、心理健康、知识普及三个方面展开，结合元旦、春节、中秋、重阳等节点，加入音乐鉴赏、绘画、手工、科技制作等趣味活动，在一对一交流中建立长期信任。'
      },
      {
        title: '影响与延展',
        body:
          '项目逐步形成“学校与慧灵基地”“杭电学子与慧灵人士”两条主线结合的结构，并在 2021 年获得精品立项、校优秀志愿基地等认可。2025 年又进一步延展出“西湖益行”公众倡导活动，把对残障群体的理解从基地内部带到公共空间。'
      }
    ]
  },
  {
    slug: 'huyoutongxin',
    name: '护佑童心',
    subtitle: '缘起微光，童心为岸',
    category: '病房陪伴',
    startYear: 2017,
    summary:
      '与浙江大学医学院附属儿童医院共同发起，围绕长期住院患儿开展阅读推广、科普讲座与艺术工作坊，让病房成为传递希望的成长空间。',
    impact: ['2017-2025', '400+ 次服务', '6600+ 志愿者', '72000 小时', '20000+ 患儿受益'],
    highlights: ['2025 青年志愿服务项目大赛优秀团队', '2023-2024 连续获评浙大儿院年度优秀志愿者团队', '儿童友好志愿服务团队', '人民日报健康客户端、杭州日报等报道']
    ,
    sections: [
      {
        title: '项目背景',
        body:
          '“护佑童心”于 2017 年正式启动，由浙江大学医学院附属儿童医院和自动化青协共同发起，主要面向长期住院患儿，聚焦病房中普遍存在的心理关怀不足、学习连续性断裂与社交支持不足等问题。'
      },
      {
        title: '服务内容',
        body:
          '项目围绕阅读推广、科普讲座、艺术工作坊等形式持续展开。2025 年的活动特别聚焦节日庆典、传统文化、科学启蒙、科技融合和兴趣培养五个维度，包括冬至彩泥饺子、未来小车奇遇记、非遗剪纸、小小航天员、重阳敬老手作等。'
      },
      {
        title: '项目成效',
        body:
          '据 1200 份有效调研数据显示，活动在转移患儿痛苦注意力、提升学习兴趣、促进交流意愿等方面都产生了清晰效果；同时近 80% 家长反馈孩子表达意愿增强、对出院生活更有期待。项目也因此获得多项医院与社会层面的认可。'
      }
    ]
  },
  {
    slug: 'smart-car',
    name: '智能汽车竞赛志愿',
    subtitle: '以志愿微光，映赛场荣光',
    category: '赛事保障',
    startYear: 2025,
    summary:
      '自动化青协全程牵头统筹第二十届全国大学生智能汽车竞赛浙江赛区选拔赛与全国总决赛的志愿服务，完成从筹备到收尾的全链条保障。',
    impact: ['省赛 236 人', '国赛 284 人', '2848.85 小时', '5804.3 小时', '200+ 志愿者专业团队'],
    highlights: ['六大组别精细分工', '五天赛前筹备', '从 7:30 到 22:00 全天候值守', '获赛事组委会与参赛高校认可']
    ,
    sections: [
      {
        title: '赛事角色',
        body:
          '在 2025 年承办的第二十届全国大学生智能汽车竞赛中，自动化青协作为唯一牵头统筹单位，负责志愿服务全链条组织，从招募、培训到现场运行与收尾均由协会主导推进。'
      },
      {
        title: '组织方式',
        body:
          '为适应大型赛事高标准需求，志愿者被划分为场内组、志愿者保障组、会务组、随队组、开闭幕式组和核心骨干组，并建立指导老师、骨干成员、岗位志愿者三级联动机制，通过标准化手册与模拟演练保证执行质量。'
      },
      {
        title: '成果回顾',
        body:
          '省赛与国赛分别实现 236 人次、284 人次签到，总时长分别达到 2848.85 小时与 5804.3 小时。项目不仅保障了赛事高效运转，也让志愿者在高压场景中完成了组织能力和责任意识的双重成长。'
      }
    ]
  },
  {
    slug: 'summer-teaching',
    name: '暑期支教',
    subtitle: '翰墨梦想翼，云衢筑梦行',
    category: '乡村支教',
    startYear: 2014,
    summary:
      '暑期支教由飞鸟夏令营、筑梦前陈、逐梦清溪三条基地线共同构成，在祁门、浦江、大溪三地持续开展课堂陪伴、地方实践与媒体传播，让乡村教育支持不止发生在教室里，也持续被社会看见。',
    impact: ['筑梦前陈始于 2014', '飞鸟夏令营始于 2019', '逐梦清溪形成于 2019', '400+ 公里双向奔赴', '2.8 万 / 7.8w+ 浏览量'],
    highlights: ['三基地并行推进，形成长期支教网络', '课堂、家访、调研与地方文化体验并行', '飞鸟 2.8 万、前陈 7.8w+、清溪多平台传播形成回响', '高校青年力量与乡村教育真实连接'],
    subProjects: [
      {
        name: '飞鸟夏令营',
        startYear: 2019,
        summary: '与安徽省黄山市祁门县柏溪中心学校建立长期合作，以六载深耕、400+ 公里双向奔赴和多元课堂设计连接高校力量与皖南乡村教育。'
      },
      {
        name: '筑梦前陈',
        startYear: 2014,
        summary: '扎根金华市浦江县黄宅镇前陈村，十一年持续开展暑期支教，以两周高密度陪伴、家访和课业巩固让“赋能成长、传递温暖”落到实处。'
      },
      {
        name: '逐梦清溪',
        startYear: 2019,
        summary: '以台州温岭大溪镇“小候鸟”为主要服务对象，将推普、爱国主题、非遗体验、茶园调研和多平台传播组合成完整实践链条。'
      }
    ],
    sections: [
      {
        title: '多点支教网络',
        body:
          '自动化青协的暑期支教不是单一项目，而是由飞鸟夏令营、筑梦前陈、逐梦清溪三个团队共同组成的多点实践网络。三支团队分别对应黄山祁门、金华浦江、台州大溪等服务地，覆盖乡村儿童陪伴、课程设计、家访调研、文化体验与社会传播。'
      },
      {
        title: '代表项目',
        body:
          '飞鸟夏令营自 2019 年起持续开展，与安徽省黄山市祁门县柏溪中心学校建立长期合作；筑梦前陈基地自 2014 年起运行，连续十一年服务前陈村；逐梦清溪团队形成于 2019 年，围绕大溪镇“小候鸟”与乡村儿童服务展开。'
      },
      {
        title: '传播与价值',
        body:
          '这些支教活动不只发生在课堂里，也被越来越多人看见。飞鸟夏令营专题报道浏览量超过 2.8 万，筑梦前陈相关内容达到 7.8w+，逐梦清溪也获得学习强国、潮新闻、掌上温岭等平台关注，社会影响力在持续扩大。'
      }
    ]
  }
]

export const extendedStories: ProjectStory[] = [
  {
    slug: 'xihu-yixing',
    name: '西湖益行',
    subtitle: '把理解带到湖畔，把尊重带回日常',
    category: '公众倡导',
    startYear: 2025,
    summary:
      '作为灵动翼行的公众倡导延展，自动化青协于 2025 年 4 月 26 日在西湖举办公益徒步与互动体验，让更多游客理解慧灵学员与残障群体。',
    impact: ['300+ 游客参与', '96 名志愿者', '7 小时坚守', '5 公里路线', '500+ 手册分发'],
    highlights: ['六个互动摊位', '20+ 祝福视频', '50 把漆扇认领一空', '把偏见转化为具体的理解行动']
  },
  {
    slug: 'donghu',
    name: '东湖街道养老服务',
    subtitle: '陪伴长者，让岁月更温柔',
    category: '养老服务',
    startYear: 2025,
    summary:
      '2025 年，自动化青协在东湖街道养老服务中心持续开展敬老陪伴活动，从重阳手作到冬日烘焙，再到老年学校结业典礼，用一次次见面把“陪伴”落到长者日常里。',
    impact: ['3 场重点主题活动', '覆盖 10-12 月连续服务', '手作 + 烘焙 + 文艺陪伴多场景联动'],
    highlights: ['10 月 22 日重阳艾草锤手作活动', '11 月 12 日共做桃酥活动', '12 月 24 日老年学校结业典礼志愿服务', '志愿者一对一协助，重视交流与情感陪伴'],
    sections: [
      {
        title: '年度陪伴概览',
        body:
          '“莫道桑榆晚，为霞尚满天。”2025 年，自动化青协与东湖街道养老服务中心携手，围绕重阳节、冬季手作和年度结业典礼开展多场志愿活动。活动既有动手实践，也有文艺陪伴与现场服务，重点是让长者在熟悉的节奏里感受到持续的关心和尊重。'
      },
      {
        title: '10 月 22 日｜爱在重阳，情暖金秋',
        body:
          '重阳节当天，志愿者走进东湖街道养老服务中心，和长者一起制作手工艾草锤。手工老师先讲解艾草锤的寓意和步骤，随后志愿者进行一对一协助，从穿线、填装到缝合全程陪伴。活动现场交流自然、气氛轻松，长者们完成作品后合影留念。'
      },
      {
        title: '11 月 12 日｜酥香满厅堂，温情共朝夕',
        body:
          '在养老院烘焙活动中，志愿者与长者一起完成桃酥制作。大家从筛粉、揉面、塑形到等待出炉分工合作，现场充满笑声。随着烤箱升温，香气弥漫整个活动空间。对长者来说，这不仅是一次手作体验，更是一次被认真陪伴的温暖午后。'
      },
      {
        title: '12 月 24 日｜结业典礼里的欢歌笑语',
        body:
          '东湖居家养老服务中心老年学校举行 2025 年度结业典礼，自动化青协志愿者参与现场保障与陪伴服务。典礼中有管乐、合唱、戏曲联唱、二胡和舞蹈等节目，《五星红旗》《祖国不会忘记》等曲目将气氛推向高潮。结业典礼既是阶段总结，也让长者们在舞台上被看见、被喝彩。'
      },
      {
        title: '温情相伴，薪火相传',
        body:
          '回看这一年的东湖服务，从艾草锤到桃酥，再到结业典礼，青协团队把敬老爱老做成了可持续的日常行动。未来将继续围绕长者真实需求，探索更多有温度的服务形式，让“奉献、友爱、互助、进步”的志愿精神在陪伴中延续。'
      }
    ]
  },
  {
    slug: 'ocean-heaven',
    name: '海洋天堂',
    subtitle: '心向暖阳，青志未央',
    category: '年度专题',
    startYear: 2025,
    summary:
      '面向东湖、九堡、临平、丁桥、闸弄口五个基地持续开展陪伴服务，以节庆活动、手工互动与情感联结把全年 27 次行动织成一条温暖主线。',
    impact: ['5 个基地', '27 次活动', '覆盖双节、重阳、冬至与元旦'],
    highlights: ['五基地全年联动开展 27 次活动', '覆盖重阳、冬至、双节、元旦等节庆场景', '香囊、花样饺子、音乐节、祈福牌形成多样互动', '以陪伴、理解、包容为核心方法'],
    subProjects: [
      {
        name: '东湖慧灵基地',
        summary: '以 10 月 29 日重阳陪伴为代表，围绕香囊制作与节日问候展开互动，把敬老爱老与助残扶残的心意落在安静而真诚的陪伴里。'
      },
      {
        name: '九堡慧灵基地',
        summary: '以冬至专题活动为核心，通过 PPT 科普节气由来与传统习俗，再一起制作花样饺子，让民俗体验、协作互动与冬日温情自然发生。'
      },
      {
        name: '临平慧灵基地',
        summary: '围绕国庆与中秋双节开展破冰互动和桂花香囊制作，在节日文化讲解与共同创作中传递团圆暖意与彼此关怀。'
      },
      {
        name: '丁桥慧灵基地',
        summary: '以 11 月音乐节活动为代表，志愿者与学员轮番演唱、合作合唱，用旋律建立更直接的互动，让陪伴在舞台与掌声之间被看见。'
      },
      {
        name: '闸弄口慧灵基地',
        summary: '在元旦前夕通过习俗介绍、抽奖互动和心愿祈福牌制作迎接新年，让祝福表达、分享愿望与集体留影成为辞旧迎新的温暖仪式。'
      }
    ],
    sections: [
      {
        title: '年度概览',
        body:
          '2025 年，“海洋天堂”在东湖、九堡、临平、丁桥、闸弄口五个基地持续开展。全年共 27 次活动，覆盖重阳、冬至、国庆中秋双节和元旦等时间点，也包含音乐互动、手工创作和日常陪伴。对我们来说，重点不是做了多少场活动，而是把陪伴真正做成长期、稳定的服务。'
      },
      {
        title: '五个基地的陪伴现场',
        body:
          '东湖慧灵基地以重阳主题陪伴展开，志愿者和学员们一起制作香囊，在秋日节庆里传递安康祝愿与敬老爱老的心意；九堡慧灵基地围绕冬至主题，通过习俗讲解和彩色橡皮泥花样饺子制作，让传统民俗在互动中变得可感可亲；临平慧灵基地将国庆与中秋双节结合起来，用破冰游戏和桂花香囊制作营造家国同庆、团圆相伴的温暖氛围；丁桥慧灵基地以 11 月音乐节为亮点，志愿者与慧灵学员轮流演唱、合唱，在掌声与节奏中拉近彼此距离；闸弄口慧灵基地则在元旦前夕通过习俗分享、抽奖互动和心愿祈福牌制作，让学员们把对新一年的愿望和祝福郑重表达出来。'
      },
      {
        title: '活动中的共同方法',
        body:
          '从五个基地的活动内容可以看见，“海洋天堂”形成了较为稳定的陪伴方法：先从重阳、冬至、双节、元旦等熟悉的节庆语境切入，降低参与门槛；再通过香囊、花样饺子、祈福牌、音乐表演等具体可参与的活动，让服务对象在动手、表达与合作中自然投入；配合知识讲解、主持带动、破冰互动和一对一协助，把志愿服务从“完成流程”转向“建立关系”。这种方式强调的不只是活动热闹，而是让理解、尊重与包容在一次次轻量但真实的陪伴中被持续积累。'
      },
      {
        title: '心向暖阳，青志未央',
        body:
          '回望这一年的海洋天堂专题，最重要的并不是某一场活动形式本身，而是志愿者始终陪伴在残障伙伴身边的长期坚持。耐心倾听、温柔鼓励、共同完成一件小作品、一起唱完一首歌、认真说出新年愿望，这些看似细小的时刻，共同构成了项目真正的价值。它让人看到，身体的残缺并不是生命的边界，只要有真诚陪伴、深刻理解与包容的环境，每一个生命都能在爱与尊重中持续发光。这也是“心向暖阳，青志未央”这句主题在页面中需要被完整保留下来的原因。'
      }
    ]
  }
]

export const annualMoments: AnnualMoment[] = [
  {
    title: '西湖益行',
    context: '2025 年 4 月 26 日',
    description: '以 5 公里公益徒步、互动体验与公众对话，向 300+ 游客讲述理解与包容的故事。',
    tag: '湖畔理解行动'
  },
  {
    title: '东湖养老',
    context: '2025 年 10-12 月',
    description: '从重阳艾草锤到桃酥烘焙，再到老年学校结业典礼，把陪伴落在长者的日常生活里。',
    tag: '长者陪伴'
  },
  {
    title: '海洋天堂',
    context: '2025 全年',
    description: '走进五个慧灵与残疾人之家基地，通过节庆活动和音乐手作持续陪伴服务对象。',
    tag: '助残专题'
  },
  {
    title: '心灵驿站',
    context: '2024 年 5 月',
    description: '通过心情石、沙子瓶 DIY 等治愈环节，引导同学表达情绪、建立校园关怀通道。',
    tag: '校园公益'
  },
  {
    title: '百团招新',
    context: '2025 年 9 月 21 日',
    description: '把青协项目和部门日常带到校园广场，让新同学更快了解并加入。',
    tag: '组织传承'
  }
]

export const internalEvents: InternalEvent[] = [
  {
    id: '2025-may-general-meeting',
    title: '2025 年度五月全体大会',
    date: '2025-05-23',
    intro:
      '5 月 23 日，自动化青协召开全体大会，围绕志愿服务复盘、团队交接与未来规划展开，成员在总结中凝聚共识，在展望中明确方向。',
    highlights: [
      '会长蒋婉欣作为最后一次主持会议，回顾了活动策划与执行中的团队协作历程，并感谢全体成员付出。',
      '会上宣布新一届主席团成员：毛佳伊、赵德昌、杨嘉欣、郑煜腾。',
      '新任会长毛佳伊表达感谢与责任意识，提出将坚持与成员共同进步，持续推进协会建设。',
      '各部门在展望环节汇报未来工作重点，围绕执行质量与项目持续性提出具体安排。',
      '大会同步进行优秀志愿者集体表彰，强化了组织认可与榜样引领。',
    ],
    keyPeople: ['蒋婉欣', '毛佳伊', '赵德昌', '杨嘉欣', '郑煜腾'],
    closing: '大会以“持续共建、共同成长”为主线，为新一阶段的志愿工作奠定组织基础。',
  },
  {
    id: '23rd-youth-congress',
    title: '自动化青协第二十三届青代会',
    date: '2025-05-24',
    intro:
      '5 月 24 日下午，青协举行第二十三届青年志愿者代表大会暨换届仪式，六十余名志愿者参会，完成年度复盘与组织换届。',
    highlights: [
      '指导老师叶符公葳在开场致辞中肯定年度工作成果，并指出需持续改进的工作问题。',
      '会长蒋婉欣围绕过去一年活动执行与个人成长体验进行复盘分享，向成员寄予期望。',
      '留任答辩环节中，候选人围绕能力、经历与任期构想进行陈述，展示继续服务组织的意愿。',
      '大会尾声由新任团支书赵德昌公布换届结果，并向六位新部长颁发聘书。',
      '会议通过庄重仪式与轻松互动并行（含抽奖环节），提升了成员参与感与组织凝聚力。',
    ],
    keyPeople: ['叶符公葳', '蒋婉欣', '赵德昌'],
    closing: '第二十三届青代会标志着组织顺利换届，也明确了“人可更替、志愿初心不变”的传承方向。',
  },
  {
    id: 'autumn-welcome-meeting',
    title: '自动化青协迎新见面会',
    date: '2025-10-25',
    intro:
      '10 月 25 日，自动化青协召开秋季首次全体大会暨迎新见面会，重点围绕新成员融入、组织文化传递与部门方向说明展开。',
    highlights: [
      '新任指导老师张艺珂向新干事致以问候，鼓励同学们在坚持志愿精神的同时做好学业规划。',
      '主席团依次发言，会长毛佳伊结合协会成长故事阐释志愿初心与团队凝聚力。',
      '各部门部长与新干事集中自我介绍，快速建立跨部门联系，降低新成员协作门槛。',
      '六位新任部长围绕部门职能说明未来工作规划，强调深耕志愿领域与优化服务模式。',
      '会议形成“老成员传经验、新成员明方向”的协同氛围，为秋季工作开展完成组织动员。',
    ],
    keyPeople: ['张艺珂', '毛佳伊'],
    closing: '迎新见面会完成了从“加入组织”到“进入角色”的过渡，为后续项目执行打下团队基础。',
  },
  {
    id: 'warm-youth-conference',
    title: '自动化青协暖青大会',
    date: '2025-11-29',
    intro:
      '11 月 29 日，青协举办年度暖青大会，通过总结复盘、节目展示、互动游戏与聘书颁发，强化成员归属感与组织文化认同。',
    highlights: [
      '会长毛佳伊在开场发言中回顾各部门阶段成果与后续计划，并提出具有执行导向的改进建议。',
      '各部门节目与互动环节集中呈现新老成员协同状态，进一步增强团队连结。',
      '趣味游戏与抽奖环节贯穿全场，现场参与度高，推动跨部门成员快速熟悉。',
      '团支书赵德昌在总结中提出后续发展方向，强调延展志愿服务边界与创新活动形式。',
      '负责人胡潘昊向新干事颁发聘书；活动同时评出“最佳部门——项拓”，形成阶段激励机制。',
    ],
    keyPeople: ['毛佳伊', '赵德昌', '胡潘昊'],
    closing: '暖青大会在轻松氛围中完成组织动员与价值传递，成为连接成员情感与行动目标的重要节点。',
  },
  {
    id: '2025-dec-general-meeting',
    title: '2025 年度十二月全体大会',
    date: '2025-12-26',
    intro:
      '12 月 26 日，青协召开秋季学期第二次全体大会，聚焦阶段总结、部门优化建议与优秀成员表彰，推动组织进入年度收束与新周期准备。',
    highlights: [
      '指导老师姜智敏、姜博恩致辞，强调志愿精神与学业并重，鼓励成员持续投入公益实践。',
      '主席团依次发言：会长毛佳伊提出部门改进建议，副会长杨嘉欣从实务角度拆解志愿服务路径，团支书赵德昌强调互助协作。',
      '六位部长按办公室、组织部、新媒体、志管中心、基地拓展中心、项目拓展中心顺序进行成员介绍与规划汇报。',
      '大会尾声颁发“优秀干事奖”与“优秀志愿者奖”，对阶段贡献进行公开认可。',
      '活动在集体留影与交流中收束，强化了成员间连接和组织归属。',
    ],
    keyPeople: ['姜智敏', '姜博恩', '毛佳伊', '杨嘉欣', '赵德昌'],
    closing: '大会明确了“复盘—优化—激励—再出发”的组织节奏，为下一阶段工作衔接提供清晰抓手。',
  },
]

export const timelineEvents: TimelineEvent[] = [
  {
    year: '2002',
    title: '自动化青协成立',
    description: '协会确立“服务同学，奉献社会”的宗旨，开启校园志愿服务传承。',
    badge: '起点'
  },
  {
    year: '2014',
    title: '灵动翼行与前陈支教基地起步',
    description: '助残项目和前陈暑期实践基地同步起步，形成两条长期服务线。',
    badge: '双线生长'
  },
  {
    year: '2017',
    title: '护佑童心启动',
    description: '与浙大儿院共同发起阳光病房志愿服务，将病房陪伴发展为协会核心品牌项目。',
    badge: '病房品牌'
  },
  {
    year: '2019',
    title: '飞鸟夏令营与逐梦清溪形成',
    description: '支教实践从单点陪伴扩展为多地联动，乡村课堂、家访与调研逐步成熟。',
    badge: '乡村教育'
  },
  {
    year: '2021',
    title: '灵动翼行获精品立项、校优秀志愿基地',
    description: '长期助残服务获得校内认可，项目进入稳定发展阶段。',
    badge: '荣誉节点'
  },
  {
    year: '2024',
    title: '二教记忆书吧成立',
    description: '2024 年 11 月 25 日，二教记忆书吧志愿活动正式成立，校园公益空间由临时协助延伸到常态化阅读与空间运营服务。',
    badge: '校园服务'
  },
  {
    year: '2025',
    title: '年度重点项目全面推进',
    description: '全年共开展 197 场有效志愿服务活动，西湖益行、智能车双赛保障、暑期支教、海洋天堂、文思小学与书吧项目共同构成这一年的高光。',
    badge: '第二十三载'
  }
]

export const serviceMatrix: StatItem[] = [
  {
    label: '社区与基地',
    value: '五大校外基地',
    detail: '早城、下沙、浙大儿院、东湖养老、文思小学及多类常态化校外合作场景，围绕老人、儿童与家庭持续服务'
  },
  {
    label: '慧灵助残',
    value: '五个慧灵基地',
    detail: '东湖、九堡、临平、丁桥、闸弄口长期联动，经过 12 年发展形成稳定陪伴体系'
  },
  {
    label: '病房陪伴',
    value: '护佑童心',
    detail: '每年开展 20+ 次课程，为住院患儿设计趣味课堂，相关成果获得主流媒体与项目赛事认可'
  },
  {
    label: '乡村支教',
    value: '三支队伍并行',
    detail: '飞鸟夏令营、筑梦前陈、逐梦清溪把课程、活动、调研与地方文化体验连接起来'
  },
  {
    label: '校园与赛会',
    value: '书吧 + 智能车',
    detail: '从二教书吧、智慧空间、迎新服务到智能车省赛国赛保障，形成校内日常与大型赛会双线协同'
  }
]

export const honors: StatItem[] = [
  {
    label: '护佑童心',
    value: '优秀团队',
    detail: '获优秀志愿者团队、儿童友好志愿服务团队、2025 青年志愿服务项目大赛优秀团队'
  },
  {
    label: '浙大儿院合作',
    value: '连续认可',
    detail: '2023-2024 连续获评浙大儿院年度优秀志愿者团队'
  },
  {
    label: '灵动翼行',
    value: '精品立项 / 省赛铜奖',
    detail: '连续多年被评为校级精品立项，2021 年获校优秀志愿基地，2024 年获浙江省青年志愿服务项目大赛铜奖'
  },
  {
    label: '传播影响',
    value: '61 篇推文 + 13 个视频',
    detail: '杭电自动化青协全年发布推文 61 篇、活动视频 13 个，并获杭州日报、人民日报健康客户端、中国青年报、潮新闻等平台关注'
  }
]

export const associationFacts: StatItem[] = [
  {
    label: '成员规模',
    value: '63 名',
    detail: '由姜智敏、姜博恩老师指导，下设六个职能部门'
  },
  {
    label: '注册志愿者',
    value: '5103 人',
    detail: '形成稳定的组织蓄水池和活动参与基础'
  },
  {
    label: '累计时长',
    value: '80024.7 小时',
    detail: '最直观的结果是服务方式越来越成熟，不靠单次活动撑场面'
  },
  {
    label: '2025 年活动',
    value: '197 场',
    detail: '覆盖环境卫生、扶残助残、便民生活、禁毒宣传等多个领域'
  }
]

export const majorDeeds: FeatureBlock[] = [
  {
    title: '常规活动：扎根社区，服务社会',
    summary:
      '自动化青协在社区、医院、养老与小学基地保持长期签约合作，也在慧灵基地和校园公共空间中持续开展常态化服务，让“长期陪伴”真正成为组织方法。',
    bullets: [
      '早城社区、下沙社区、浙大儿院、东湖养老、文思小学等基地长期联动，围绕老人、儿童和家庭开展服务',
      '在浙大儿院每年开展 20+ 次课程，相关成果获杭州日报、人民日报健康客户端等媒体关注，并在 2025 年获青年志愿服务项目大赛优秀团队',
      '五个慧灵基地经过 12 年发展逐步形成稳定的活动体系，多次被杭州慧灵与校院官方平台报道',
      '2024 年 11 月新增二教书吧志愿项目，已有 910 名志愿者投入，累计服务 4700+ 小时'
    ]
  },
  {
    title: '暑期支教：播撒希望，筑梦未来',
    summary:
      '2025 年暑期，自动化青协在安徽祁门、浦江前陈、温岭大溪三地同步开展支教实践，把课堂陪伴、文化体验与地方调研结合起来，形成多点支教网络。',
    bullets: [
      '飞鸟夏令营在祁门柏溪中心学校开设文化巩固、艺术表达、科学探索和综合素质课程',
      '筑梦前陈在前陈村以语文、数学、英语、科学课程结合趣味活动，持续激发乡村儿童学习兴趣',
      '逐梦清溪在大溪镇党群活动中心带领“小候鸟”参与文化礼堂参访、非遗体验、运动会和产业调研',
      '三支队伍共同把“课堂 + 活动 + 实践 + 传播”做成了支教项目的完整闭环'
    ]
  },
  {
    title: '大型赛会：志愿之光，闪耀杭城',
    summary:
      '除了长期基地服务，自动化青协在公众倡导和大型科技赛事中也承担了高密度、高标准的组织任务，把青年志愿者的执行力带到更大公共场景。',
    bullets: [
      '2025 年 4 月 19 日联合举办西湖益行，96 名志愿者在 5 公里路线和 7 个摊位中持续服务 7 小时',
      '第二十届全国大学生智能汽车竞赛省赛、国赛在校举办期间，志愿者完成场地、引导、秩序、物资与答疑等全流程保障',
      '200+ 名省赛志愿者和近 300 名国赛志愿者累计服务 8000+ 小时，成为赛事稳定运行的重要支撑',
      '大型活动证明协会不仅能做温情陪伴，也能完成复杂场景下的系统化组织'
    ]
  },
  {
    title: '展望未来：不忘初心，砥砺前行',
    summary:
      '2025 年自动化青协全年共开展 197 场有效志愿服务活动，内容横跨环境卫生、扶残助残、便民生活、禁毒宣传等多个领域，组织传播和社会影响也同步扩大。',
    bullets: [
      '“杭电自动化青协”公众号全年发布推文 61 篇、活动视频 13 个，学院公众号转载 12 篇',
      '三支支教团队全年发布 24 篇推文，其中逐梦清溪、飞鸟夏令营等项目累计阅读量达到 10 万+ 级别',
      '组织希望以更创新的形式持续开展志愿活动，让更多社会力量看见并加入公益行动',
      '面向 2026 年，协会将继续在长期项目、校园服务和社会联动中扩大品牌影响力'
    ]
  }
]

export const teachingBaseProfiles: TeachingBaseProfile[] = [
  {
    name: '飞鸟夏令营',
    location: '安徽省黄山市祁门县柏溪中心学校',
    since: '2019',
    theme: '六载深耕 · 双向奔赴',
    summary:
      '飞鸟夏令营自 2019 年起持续走进祁门县柏溪中心学校，六年来跨越 400 多公里山海，把高校青年力量、乡村教育支持与本地文化理解连接成一条稳定的暑期实践线。',
    metrics: ['2019 起持续开展', '400+ 公里双向奔赴', '2.8 万浏览专题报道', '14 篇原创推文', '18 篇图文/短视频'],
    highlights: ['祁门长期合作', '2025 多维筹备机制', '科学 / 艺术 / 梦想课堂', '地方政府与主流媒体持续关注'],
    background:
      '项目源于对乡村教育资源均衡的长期关切，与祁门县柏溪中心学校建立稳定合作后，团队每年七月都会奔赴皖南山村。飞鸟并不把支教理解为单向输出，而是坚持“让课堂成为发现的乐园”，围绕乡村孩子真实需求设计课程，让孩子们在知识启蒙中看见更大的世界，也让大学生在长期实践中理解教育公平的真实含义。',
    practice:
      '2025 年五月起，团队围绕课程、安全、宣传、财务等维度展开系统筹备。七月开营后，科学拓展课通过“水瓶琴”等动手实践带孩子们感受声学乐趣，艺术创作课把皖南山水、古村与家乡记忆带进画面，“梦想主题班会”则让志愿者以自己的求学经历回应孩子们对未来的好奇。项目的重点不是堆叠课程数量，而是把趣味、表达和成长感一起交给孩子们。',
    impact:
      '飞鸟夏令营把高校智慧、青年陪伴和乡土文化理解连接起来，让课堂不止停留在知识传授层面，也成为孩子建立自信、表达理想和重新认识家乡的入口。六年来的持续推进，使其逐步成长为自动化青协最具辨识度的乡教品牌之一。',
    mediaSummary:
      '飞鸟的传播重点放在“六载深耕”“400 公里双向奔赴”和“高校如何参与乡村教育振兴”这几个角度。2025 年既有主流媒体专题报道，也有地方教育平台关注和团队自媒体持续记录。',
    mediaHits: [
      {
        platform: '杭州新闻',
        angle: '青访六载路 / 乡村教育振兴新图景',
        reach: '2.8 万浏览',
        value: '把飞鸟六年的行动讲得更清楚，让更多人看懂项目价值'
      },
      {
        platform: '潮新闻校友圈',
        angle: '400 公里的双向奔赴',
        reach: '专题跟进',
        value: '突出高校志愿服务的责任与青年担当'
      },
      {
        platform: '祁门教育',
        angle: '地方教育系统纪实',
        reach: '平台关注',
        value: '强调项目对乡村孩子视野拓展与乡土文化传承的现实价值'
      },
      {
        platform: '团队新媒体账号',
        angle: '课程 / 家访 / 调研实时记录',
        reach: '14 篇推文 + 18 篇图文短视频',
        value: '把项目过程完整记录下来，方便传播和复盘'
      }
    ]
  },
  {
    name: '筑梦前陈',
    location: '浙江省金华市浦江县黄宅镇前陈村',
    since: '2014',
    theme: '十一年坚守 · 赋能成长',
    summary:
      '筑梦前陈自 2014 年成为自动化学院暑期实践基地以来，已连续十一年走进前陈村。团队通过两周陪伴、学业辅导和兴趣启蒙，把“赋能成长、传递温暖”落到每年暑期的具体行动里。',
    metrics: ['2014 起持续十一年', '两周集中陪伴', '7.8w+ 浏览量', '家访 + 反思会议', '最佳实践团队'],
    highlights: ['前陈村长期基地', '课业巩固与兴趣课程并行', '趣味运动会与家访协同', '最佳实践团队 / 高传播回响'],
    background:
      '“筑梦前陈”支教基地坐落于浦江县前陈村，是自动化学院最稳定的暑期实践基地之一。十一年来，志愿者们每到盛夏都会如期赴约，不只传递文化知识，也持续把兴趣培养、成长陪伴和乡村儿童假期支持纳入项目结构。这个基地最重要的价值，在于它不是短促的体验式活动，而是一条已经被时间验证过的长期陪伴关系。',
    practice:
      '2025 年 7 月中下旬，团队围绕学业辅导、基础学科巩固和兴趣课程展开两周陪伴。语文、数学、英语、科学等课程帮助孩子们查漏补缺，趣味课堂、团队游戏、电影赏析、音乐互动、趣味运动会等活动则让假期不再单调。与此同时，团队通过家访更深入理解孩子们的日常环境，并通过每日反思会议及时调整节奏与方法，让项目保持温度，也保持务实。',
    impact:
      '对孩子们而言，这段支教时光意味着更扎实的课业基础、更多元的兴趣启蒙和对未来更具体的想象；对团队成员而言，这是一次把专业能力、公众表达和社会责任感真正放到真实情境中的实践。十一年的连续开展，也让筑梦前陈成为“长期支教如何改变人”的一个稳定样本。',
    mediaSummary:
      '筑梦前陈的传播重点更强调“坚守”“文明火种”“教育公平”和“最佳实践团队”。它既有服务地现场的真实温度，也有在公众平台上被广泛看见的高传播成果。',
    mediaHits: [
      {
        platform: '杭州新闻',
        angle: '支教锐评 / 十一年坚守',
        reach: '7.8w+ 浏览',
        value: '让“温暖陪伴”和“教育公平”被更多公众看见'
      },
      {
        platform: '项目评选成果',
        angle: '最佳实践团队',
        reach: '成果展示',
        value: '证明项目不只感人，也具备扎实的组织与实践质量'
      },
      {
        platform: '团队纪实内容',
        angle: '家访 / 运动会 / 反思会议',
        reach: '持续记录',
        value: '把两周支教过程记录成有温度也有方法的实践档案'
      }
    ]
  },
  {
    name: '逐梦清溪',
    location: '浙江省台州市温岭市大溪镇党群活动中心',
    since: '2019',
    theme: '仲夏筑梦 · 候鸟守护',
    summary:
      '逐梦清溪成立于 2019 年，以大溪镇“小候鸟”和乡村儿童为主要服务对象，把假期陪伴、课程教学、地方文化体验和基层传播连接成一条更立体的实践线。',
    metrics: ['2019 起持续开展', '小候鸟陪伴', '多平台传播', '茶园 / 非遗 / 运动会', '学习强国等平台关注'],
    highlights: ['推普与爱国主题课程', '手工制作与团队游戏', '茶园调研与非遗体验', '学习强国 / 潮新闻 / 掌上温岭等平台传播'],
    background:
      '“逐梦清溪”以“仲夏筑梦，候鸟守护”为宗旨，在学校老师和大溪镇政府支持下逐步形成。项目始终高度重视留守儿童和“小候鸟”的假期陪伴、学业辅导、自护教育、文体活动和微心愿实现，希望通过更丰富、更个性化的教育服务，让乡村儿童在假期中也能获得持续支持。',
    practice:
      '团队在课程设计上兼顾知识启蒙与价值引导，开设推普、爱国主题等教育课程，并通过手工制作、团队游戏培养孩子们的动手能力与团队意识。除了常规课堂，团队还带孩子们走进方山茶博园实地调研、感受千年茶文化，在文化礼堂与非遗体验中触摸地方传统，在主题运动会和户外活动中建立协作意识。项目因此不只是一组课堂，而是一套“课程 + 活动 + 实地体验 + 基层服务”的完整实践结构。',
    impact:
      '逐梦清溪最突出的价值，在于它把支教和乡村振兴、基层服务、文化传播结合得更紧密。项目既切实服务了孩子的成长，也让团队成员在教学、组织、协作和问题解决中快速成长，形成“服务有实效、传播有影响、青年有担当”的清晰形象。',
    mediaSummary:
      '逐梦清溪的传播覆盖面很广，既有学习强国、潮新闻、杭州新闻等主流平台，也有望潮、掌上温岭和青协公众号等渠道，项目被更多人看见。',
    mediaHits: [
      {
        platform: '学习强国',
        angle: '基层服务与正能量实践',
        reach: '专题刊发',
        value: '为项目提供了更高层级的公共传播背书'
      },
      {
        platform: '潮新闻 / 杭州新闻',
        angle: '实践亮点与青年担当',
        reach: '主流媒体集中报道',
        value: '强化了项目在市级媒体中的可见度与公共影响'
      },
      {
        platform: '望潮 / 掌上温岭',
        angle: '地方服务实效',
        reach: '地方深度报道',
        value: '让服务地对项目成效有更具体的在地认知'
      },
      {
        platform: '青协公众号 / 抖音 Vlog',
        angle: '图文 + 短视频',
        reach: '持续更新',
        value: '把项目成果转化为更适合青年传播的内容形态'
      }
    ]
  }
]

export const teachingMediaWall: TeachingMediaHit[] = teachingBaseProfiles.flatMap((base) =>
  base.mediaHits.map((item) => ({
    ...item,
    angle: `${base.name} · ${item.angle}`
  }))
)

export const departments: DepartmentInfo[] = [
  {
    name: '办公室',
    focus: '统筹协调',
    description: '负责协会日常联络、会议组织、制度推进与内部协作，让组织运行更稳。'
  },
  {
    name: '组织部',
    focus: '活动组织',
    description: '负责全体大会、暖青大会、迎新与内部活动流程设计，是组织节奏的核心执行者。'
  },
  {
    name: '新媒体',
    focus: '内容传播',
    description: '负责推文、视频、海报和活动纪实，把每一次志愿行动转化为可传播的温度。'
  },
  {
    name: '志愿者管理中心',
    focus: '保障调度',
    description: '负责报名、排班、培训与现场保障，让每场活动都有人、有序、可持续。'
  },
  {
    name: '基地拓展中心',
    focus: '基地联动',
    description: '负责与社区、医院、慧灵基地等合作方建立长期联系，拓展服务落点。'
  },
  {
    name: '项目拓展中心',
    focus: '项目策划',
    description: '围绕重点项目和新公益议题做策划执行，把想法落实成具体服务。'
  }
]

export const teacherMoments: StatItem[] = [
  {
    label: '2025 青代会',
    value: '叶符公葳',
    detail: '在换届节点回顾过去、指出问题并对未来提出展望'
  },
  {
    label: '2025 迎新见面会',
    value: '张艺珂',
    detail: '鼓励新成员在青协这个温暖集体中坚守志愿精神'
  },
  {
    label: '2025 十二月全体大会',
    value: '姜智敏、姜博恩',
    detail: '按 2025 年度会议信息更新的指导老师名单'
  }
]

export const activityColumns: ProjectStory[] = [
  signatureProjects[1],
  {
    ...signatureProjects[0],
    slug: 'lingdongyixing',
    name: '灵动翼行 / 西湖益行',
    subtitle: '从基地陪伴到公众倡导',
    impact: ['535 次服务', '2800+ 志愿者', '15525.8 小时', '西湖 300+ 游客参与', '96 名志愿者 7 小时行动'],
    sections: [
      {
        title: '基地服务主线',
        body:
          '“灵动翼行”是自动化青协的长期助残品牌，长期与多个慧灵基地合作，通过陪伴、课程、兴趣培养和节庆活动支持心智残障人士与精神康复者。'
      },
      {
        title: '公众倡导延展',
        body:
          '2025 年，协会在此基础上发起“西湖益行”，通过徒步、展览、互动摊位和故事分享，把理解与包容带到公共空间，让更多人愿意了解并参与。'
      },
      {
        title: '年度亮点',
        body:
          '西湖益行当天共有 96 名志愿者、300+ 游客参与，5 公里路线串联六个互动摊位，分发 500+ 手册，并留下 20+ 条视频祝福与 50 把漆扇互动成果。'
      }
    ]
  },
  signatureProjects[2],
  extendedStories[1],
  extendedStories[2],
  {
    slug: 'book-bar',
    name: '二教记忆书吧',
    subtitle: '让阅读空间也成为可持续的志愿现场',
    category: '校园服务',
    startYear: 2024,
    summary:
      '自 2024 年 11 月 25 日成立以来，书吧志愿活动围绕环境维护、秩序支持、培训机制与空间运营持续展开，把二教记忆书吧建设成兼具阅读、交流与公益属性的校园文化空间。',
    impact: ['2024 年 11 月 25 日成立', '首批 14 名志愿者', '1000+ 志愿人数', '3400+ 志愿指南下载'],
    highlights: ['建立书吧志愿者培训计划', '报名方式逐步由金数据转为二课报名', '支持英语角、招聘会、妇女节送花等场景', '从日常值守走向规范化空间运营'],
    sections: [
      {
        title: '起步与探索',
        body:
          '二教记忆书吧志愿活动于 2024 年 11 月 25 日正式成立，确立“以书会友，服务社会”的宗旨，尝试把阅读空间、公益服务和校园日常连接起来。次日开启首次志愿者招募，共有 14 名志愿者报名，主要负责书籍整理、桌面清理、垃圾清扫、座椅恢复和垃圾袋更换等工作。项目从最基础的环境维护起步，先把“让书吧始终可用、好用、整洁”作为最重要的第一步。'
      },
      {
        title: '发展与完善',
        body:
          '随着书吧使用频率提升，团队建立了“书吧志愿者培训计划”，让每位志愿者系统了解每日工作内容、服务标准和空间维护要求。培训也成为收集建议、优化流程的重要环节，很多执行细节都在一次次复盘和讨论中被逐步规范。与此同时，志愿人数扩展到了 1000+，群内志愿指南下载次数达到 3400+，报名方式也从早期填写金数据逐步转向二课报名，书吧志愿开始从一项新尝试演变为更成熟、更受欢迎的校园课余活动。'
      },
      {
        title: '影响力提升',
        body:
          '书吧志愿并不只服务于安静阅读本身，还承担了很多需要即时应变的空间支持任务。周五晚上的英语角、学院招聘会、3.8 妇女节送花活动、导员碰面会等活动进入书吧后，志愿者需要快速完成场地布置、秩序维护、分区管理和人流控制。正是在这些复杂场景中，书吧团队逐步形成了更灵活的现场支持能力，也让二教记忆书吧从一个阅读空间，成长为校园公共活动的重要承载地。'
      },
      {
        title: '创新与展望',
        body:
          '接下来，书吧志愿团队仍将继续反思内部机制、监督班次落实情况，并围绕阅读推广和空间运营推出更多特色活动。这个项目的目标不仅是维持一个整洁的学习环境，更是让更多同学愿意在快节奏校园生活中走进书吧、慢下来、读进去、留得住，真正把“书香满城，阅读无界”的愿景落在校园日常里。'
      }
    ]
  },
  {
    slug: 'wensi-primary',
    name: '文思小学志愿活动',
    subtitle: '在校园赛场与绳场里陪伴孩子成长',
    category: '校地共建',
    startYear: 2025,
    summary:
      '2025 年，自动化青协持续与文思小学联动，围绕春季运动会和冬季跳绳比赛开展秩序维护、现场协助和陪伴助力，把志愿服务落在儿童成长最具体的校园场景中。',
    impact: ['春季运动会保障', '冬季跳绳比赛助力', '秩序维护', '安全看护'],
    highlights: ['春日赛场与冬日绳场形成全年呼应', '志愿者兼顾秩序、鼓励与安全支持', '帮助孩子在比赛中建立自信与快乐', '延续与文思小学的稳定合作关系'],
    sections: [
      {
        title: '春日逐梦，赛场护航',
        body:
          '2025 年春季运动会期间，自动化青协志愿者早早进入文思小学校园，在跑道旁、沙坑边和检录处承担秩序维护与现场协助工作。他们协助老师引导运动员有序参赛，为小选手们加油鼓劲，递上温水和鼓励，也细心照看场边休息的孩子，及时应对小擦伤和突发情况。项目的价值不只体现在“维持秩序”，更在于让孩子们在热烈竞赛中感受到安全、陪伴和被支持。'
      },
      {
        title: '绳彩飞扬，跃动冬日',
        body:
          '到了十二月，文思小学的冬季跳绳比赛成为另一场重要合作场景。志愿者们提前协助老师布置场地、整理跳绳和划分赛道，比赛中担任计数员与安全员，认真记录每位孩子的成绩，并持续提醒大家注意安全。休息间隙，他们还陪孩子们练习跳绳技巧、分享动作经验，让比赛之外也保留交流与鼓励。这份陪伴让比赛不只是一次体能挑战，也成为孩子们建立自信和收获快乐的过程。'
      },
      {
        title: '服务方式与项目价值',
        body:
          '文思小学志愿活动的特点，在于服务场景明确、对象清晰、反馈直接。无论是春季运动会还是冬季跳绳赛，志愿者都处在最靠近孩子们的现场位置，需要同时处理秩序、安全、协助和情绪支持。这样的项目虽然不以复杂策划见长，却能最直接地检验责任心、执行力和陪伴意识，也让“服务同学，奉献社会”的理念在校地合作中得到非常具体的表达。'
      },
      {
        title: '携手共进，点亮未来',
        body:
          '从春日的奔跑到冬日的跳跃，自动化青协与文思小学在 2025 年继续保持了稳定合作。对孩子们而言，志愿者的存在是赛场边的一份安心和鼓励；对志愿者而言，这些现场也是一次次理解责任、学习耐心和积累温度的过程。后续项目可以继续沿着校园赛事、成长陪伴和儿童支持等方向深化，让合作不只停留在单次活动，而是形成更连续的儿童友好服务关系。'
      }
    ]
  },
  signatureProjects[3]
]

export const activityDetailMap: Record<string, ProjectStory> = Object.fromEntries(
  activityColumns.map((project) => [project.slug, project])
)

export const activitySlices: AnnualMoment[] = [
  {
    title: '冬至包饺子',
    context: '护佑童心 · 2025 年 12 月 19 日',
    description: '用五彩软泥替代真实食材，让病房里的孩子在安全体验中感受冬至传统与陪伴。',
    tag: '病房节气活动'
  },
  {
    title: '小小航天员',
    context: '护佑童心 · 2025 年 4 月 16 日',
    description: '通过绘本、动画短片与火箭手工制作，把航天启蒙带进阳光病房。',
    tag: '病房科普'
  },
  {
    title: '重阳艾草锤',
    context: '东湖街道 · 2025 年 10 月 22 日',
    description: '在穿针引线与节气手作中，把敬老爱老的传统情感落到真实陪伴。',
    tag: '养老服务'
  },
  {
    title: '西湖 5 公里公益倡导',
    context: '西湖益行 · 2025 年 4 月 26 日',
    description: '六个互动摊位串起理解教育、义卖、视频祝福与公益体验，让湖畔成为公众课堂。',
    tag: '公众倡导'
  },
  {
    title: '九堡冬至花样饺子',
    context: '海洋天堂 · 冬至专题',
    description: '在九堡慧灵基地以 PPT 科普与橡皮泥手作传递冬至文化与基地陪伴。',
    tag: '助残陪伴'
  },
  {
    title: '百团招新',
    context: '校园招新 · 2025 年 9 月 21 日',
    description: '在东操场 106 号摊位集中介绍青协六大部门和重点项目。',
    tag: '组织传播'
  }
]

export const galleryStories: GalleryStory[] = [
  {
    title: '飞鸟夏令营传播回响',
    summary:
      '主流媒体专题与地方教育平台同步跟进，飞鸟夏令营把“六载深耕”和“400 公里双向奔赴”讲成了更容易被看懂的乡教故事。',
    tags: ['飞鸟夏令营', '杭州新闻 2.8 万', '祁门教育', '新媒体发布'],
    caption: '“让乡村教育支持被更多人看见，也让孩子的成长被更认真地记录。”'
  },
  {
    title: '筑梦前陈：十一年坚持',
    summary:
      '从前陈村课堂、家访到每日反思会议，筑梦前陈把两周支教做得扎实、可复盘，也因此获得了 7.8w+ 的传播关注。',
    tags: ['筑梦前陈', '7.8w+ 浏览', '最佳实践团队', '教育公平'],
    caption: '“十一年的坚守，不只是重复出发，而是一次次把温暖送达。”'
  },
  {
    title: '逐梦清溪媒体报道',
    summary:
      '学习强国、潮新闻、杭州新闻、望潮、掌上温岭和青协新媒体都报道了逐梦清溪，让项目成果被更多人看见。',
    tags: ['逐梦清溪', '学习强国', '潮新闻', '掌上温岭'],
    caption: '“实践不只发生在现场，也在传播中不断延展影响力。”'
  },
  {
    title: '乡村课堂与家访',
    summary:
      '飞鸟夏令营、筑梦前陈与逐梦清溪把课堂、调研、家访与地方文化结合，形成长期支教的纪实画面。',
    tags: ['暑期支教', '乡村教育', '双向奔赴'],
    caption: '“愿以知识为翼，托举孩子们飞向更辽阔的前程。”'
  },
  {
    title: '长者陪伴时刻',
    summary:
      '东湖街道养老服务与海洋天堂年度专题，以桃酥、香囊、音乐会和结业典礼记录“陪伴”作为一种日常关系的建立。',
    tags: ['东湖街道', '海洋天堂', '陪伴式服务'],
    caption: '“用陪伴传递敬意，用手作承载关怀。”'
  }
]

export const joinReasons: StatItem[] = [
  {
    label: '真实项目里成长',
    value: '长期项目线',
    detail: '从病房陪伴到赛事保障，参与的是已经持续多年的成熟项目，而不是一次性活动。'
  },
  {
    label: '在团队协作中进步',
    value: '六大部门',
    detail: '每个部门都有明确职责与实践场景，适合在策划、传播、执行与统筹中积累能力。'
  },
  {
    label: '把热爱变成行动',
    value: '五类服务方向',
    detail: '助残、病房、赛事、养老、支教与校园公益，为不同兴趣的成员提供稳定入口。'
  }
]

export const serviceInterests = ['助残关怀', '病房陪伴', '赛事志愿', '养老服务', '支教实践', '校园公益传播']

export const contactWays: StatItem[] = [
  {
    label: '公众号',
    value: '杭电自动化青协',
    detail: '关注协会官方内容，获取项目纪实、招新通知与年度活动更新。'
  },
  {
    label: '线下招新',
    value: '百团大战 / 宣讲会',
    detail: '每年通过百团大战、迎新见面会等线下场景与新成员建立联系。'
  },
  {
    label: '答疑方式',
    value: 'QQ 答疑群',
    detail: '页面保留真实渠道类型说明，不虚构具体群号与个人联系方式。'
  }
]



